/**
 * One opinion group: its name and the profile a reaction estimate reasons from.
 * The group's size is never shown. The admin edits both; an import never overwrites them (specs/opinion-datasets.md).
 */
import React, { useEffect, useState } from 'react';
import { Input, TextArea, Label, Button, Actions, StatusLine } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { updateOpinionGroup } from './opinionApi';
import { Section, SectionTitle, Preview, ErrorText } from '../DatasetWorkspace/styles';

export default function GroupEditor({ datasetId, group, onSaved }) {
  const { t } = useTranslation();
  const [label, setLabel] = useState(group.label);
  const [profile, setProfile] = useState(group.profile || '');
  const [state, setState] = useState(null); // 'saving' | 'saved' | { error }

  // A redrafted profile arrives from outside: show it
  useEffect(() => { setLabel(group.label); setProfile(group.profile || ''); }, [group.label, group.profile]);

  const dirty = label !== group.label || profile !== (group.profile || '');

  const save = async () => {
    setState('saving');
    try {
      await updateOpinionGroup(datasetId, group.key, { label, profile });
      setState('saved');
      onSaved();
    } catch (err) {
      setState({ error: err.message });
    }
  };

  return (
    <Section>
      <SectionTitle>{group.label}</SectionTitle>

      <Label htmlFor={`group-label-${group.key}`}>{t('opinionDatasets.view.label')}</Label>
      <Input id={`group-label-${group.key}`} value={label} onChange={(e) => setLabel(e.target.value)} />

      <Label htmlFor={`group-profile-${group.key}`}>{t('opinionDatasets.view.profile')}</Label>
      <TextArea
        id={`group-profile-${group.key}`}
        rows={9}
        value={profile}
        placeholder={t('opinionDatasets.view.profileEmpty')}
        onChange={(e) => setProfile(e.target.value)}
      />

      {group.profileMeta?.sourceSummary && (
        <>
          <Label as="span">{t('opinionDatasets.view.sourceSummary')}</Label>
          <Preview>{group.profileMeta.sourceSummary}</Preview>
        </>
      )}

      <Actions>
        <Button type="button" $variant="primary" disabled={!dirty || state === 'saving'} onClick={save}>
          {t('opinionDatasets.view.save')}
        </Button>
        {state === 'saved' && !dirty && <StatusLine>{t('opinionDatasets.view.saved')}</StatusLine>}
      </Actions>
      {state?.error && <ErrorText>{state.error}</ErrorText>}
    </Section>
  );
}
